/**
 * The change from one text to another, as the redline engine writes it.
 *
 * WHY
 *   A drawing that compares two texts with its own diff shows a different change
 *   from the one the engine writes: a whole Japanese sentence struck where the
 *   engine changes one word, a space added that the text never holds. A
 *   text-to-text change is drawn from here instead: the engine redlines a plain
 *   paragraph of the first text into the second, and the one renderer draws its
 *   markup (`RedlinedText`), so the drawing is the engine's change exactly.
 *
 * Nothing here is written to a document; the paragraph is built and redlined in
 * memory.
 */
import { ParagraphXml } from "../ooxml/paragraph-xml";
import { Revision } from "../ooxml/revision";
import { FIRST_REVISION_ID, RedlineEngine } from "../redline-engine";

/** The date stamped on the in-memory revisions; they are never written, so any fixed date serves. */
const IN_MEMORY_STAMP = new Date(0).toISOString();

export class TextChanges {
    /**
     * The engine's redline of `sourceText` into `requestText` as one paragraph of
     * markup, every change made by `author`: what a drawing of a text-to-text
     * change renders through the one OOXML renderer (`RedlinedText`), coloured as
     * that author's revision.
     *
     * WHY a text-to-text change
     *   A Word comment or reply the user edited, or a card's paragraph documented
     *   from its stored texts, is not read from the page, so it has no live
     *   proposal; the engine redlines a plain paragraph of the first text instead.
     *
     * EXAMPLE
     *   sourceText   "本製品の保証期間は一年とする。"
     *   requestText  "本商品の保証期間は一年とする。"
     *   author       "Jane Lawyer"
     *   paragraph    本 | w:del "製品" by Jane Lawyer | w:ins "商品" by Jane Lawyer | の保証期間は一年とする。
     *
     * RETURNS
     *   The plain paragraph of `sourceText`, with no revision in it, when the engine writes nothing,
     *   which includes two texts that differ only in spacing.
     */
    static redlinedParagraphXml(sourceText: string, requestText: string, author: string): string {
        return TextChanges.redlined(TextChanges.plainParagraphXml(sourceText), requestText, author);
    }

    /** A paragraph holding `text` in one run, with no properties; tabs and line breaks become Word's own marks. */
    private static plainParagraphXml(text: string): string {
        return ParagraphXml.plainFromText({ text, templateParagraphXml: "" });
    }

    /**
     * `paragraphXml` redlined by `author` to read `textToWrite`, through the engine every card writes with.
     *
     * WHY "asWord"
     *   The paragraph is plain text with no revision in it, so no edit reaches an own pending
     *   insertion and either writing reads the same.
     */
    private static redlined(paragraphXml: string, textToWrite: string, author: string): string {
        const engine = new RedlineEngine(new Revision({ author, stampedAt: IN_MEMORY_STAMP, firstAllocatedRevisionId: FIRST_REVISION_ID }));
        return engine.redline({ paragraphXml, textToWrite, editableSpan: null, ownChangeWriting: "asWord" }).paragraphXml;
    }
}
